<template>
  <div class="widget white_box">
    <div class="widget_header">
      <h3 class="tw-text-neutral-900 tw-text-2xl">
        AI Traffic
      </h3>
      <div
        v-if="editing && !preview"
        class="edit_buttons"
      >
        <DefaultButton
          size="small"
          variant="danger"
          @click="$parent.remove()"
        >
          Delete
        </DefaultButton>
      </div>
    </div>

    <div
      v-if="!loading && statBoxes.length > 0"
      class="statboxes"
    >
      <div
        v-for="(stat, statIndex) in statBoxes"
        :key="statIndex"
        class="stat_cards white_box"
      >
        <div class="stats">
          <div class="stat">
            {{ stat.sessions }}
          </div>
          <div class="source">
            Source:<br> Google Analytics
          </div>
        </div>
        <div class="p_body stat_title">
          <img
            :src="stat.logo"
            :alt="stat.title"
            class="stat_icon"
          >
          <span>{{ stat.title }}</span>
          <small
            :class="{
              'positive' : stat.diffDirection === '+',
              'negative' : stat.diffDirection === '-',
            }"
          >
            ({{ `${stat.diffDirection}${stat.diffAsPercentage}` }}% MoM)
          </small>
        </div>
      </div>
    </div>

    <div
      v-else-if="loading"
      class="loading_widget_data"
    >
      <i class="fa fa-btn fa-spinner fa-spin" /> Loading Data
    </div>

    <div
      v-else-if="!error"
      class="loading_widget_data"
    >
      No Data
    </div>

    <div
      v-else-if="error"
      class="widget_error"
    >
      <i class="fa fa-btn fa-exclamation-triangle" />
      <span>There's a problem with your account connection. </span>
      <a :href="`/website/settings/${activeWebsite.hash_id}`">Go to settings</a>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import axios from "axios";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";

defineProps({
  widget: {
    type: Object,
    default: null,
  },
  editing: {
    type: Boolean,
    default: false,
  }
});

const store = useStore();

const loading = ref(true);
const error = ref(false);
const trafficData = ref(undefined);

const uuid = computed(() => store.state.dashboard.uuid);
const activeWebsite = computed(() => store.state.dashboard.activeWebsite);
const preview = computed(() => store.state.dashboard.preview);

const getStatsForSource = (source, title) => {
  if (!trafficData.value || !trafficData.value.current_stats || !trafficData.value.current_stats[source]) {
    return undefined;
  }

  const currentSessions = trafficData.value.current_stats[source];
  const prevSessions = trafficData.value.prev_stats[source];

  const diff = currentSessions - prevSessions;
  let diffDirection = '';

  switch (true) {
    case currentSessions > prevSessions:
      diffDirection = '+';
      break;
    case currentSessions < prevSessions:
      diffDirection = '-';
      break;
  }

  let diffAsPercentage = 100;
  if (currentSessions > 0 && prevSessions > 0) {
    diffAsPercentage = ((Math.max(diff, diff * -1) / prevSessions) * 100).toFixed(1)
  }

  const logo = `/images/ai-logos/${source}.png`

  return {
    title,
    sessions: currentSessions,
    diffAsPercentage,
    diffDirection,
    logo,
  };
};

const statBoxes = computed(() => {
  if (!trafficData.value) {
    return [];
  }

  return [
    getStatsForSource('chatGPT', 'ChatGPT'),
    getStatsForSource('claude', 'Claude'),
    getStatsForSource('perplexity', 'Perplexity'),
    getStatsForSource('gemini', 'Google Gemini'),
  ].filter((value) => !!value);
});


onMounted(() => {
  loading.value = true;
  axios.post('/ajax/dashboard/get-widget-data', {
    type: 'aiTraffic',
    uuid: uuid.value
  }).then(response => {
    trafficData.value = response.data;
    loading.value = false;
  }).catch(err => {
    error.value = true;
    loading.value = false;
    console.log(err);
  });
});
</script>

<style scoped>
.statboxes {
  display: flex;
  justify-content: space-evenly;
  flex-flow: wrap;
  gap: 1.8rem 0;
  padding: 2rem 0;
}

.stat_cards {
  width: 50%;
  margin: 0;
  border: 1px solid #E8E5E2;
}

.stat_title {
  display: flex;
  align-items: center;
  gap: .25rem;
}

.stat_title::before {
  display: none;
}

.stat_icon {
  max-height: 1.2rem;
  max-width: 1.2rem;
  vertical-align: middle;
  display: inline-block;
}

.positive {
  color: var(--primary-color);
}

.negative {
  color: var(--negative-color);
}
</style>
